// Thin client for pkg/api. Cookie-based session auth (not a bearer token in
// localStorage), same as ai-site-gen: so every mutating request needs the CSRF token
// this module fetches once and caches, matching pkg/api/csrf.go's synchronizer-token
// pattern exactly.

let csrfToken: string | null = null

async function getCSRFToken(): Promise<string> {
  if (csrfToken) return csrfToken
  const res = await fetch('/api/csrf-token', { credentials: 'include' })
  const body = await res.json()
  csrfToken = body.csrf_token
  return csrfToken as string
}

export class ApiError extends Error {
  status: number
  // requestId mirrors the backend's request_id (pkg/api's writeJSONError): same id as
  // the X-Request-Id response header and the server-side log lines, so a support request
  // can be traced without digging through DevTools' Headers tab.
  requestId?: string
  constructor(status: number, message: string, requestId?: string) {
    super(message)
    this.status = status
    this.requestId = requestId
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {}
  let payload: string | undefined
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(body)
  }
  if (method !== 'GET') {
    headers['X-CSRF-Token'] = await getCSRFToken()
  }

  const res = await fetch(path, {
    method,
    headers,
    body: payload,
    credentials: 'include',
  })

  const text = await res.text()
  const data = text ? JSON.parse(text) : {}

  if (!res.ok) {
    throw new ApiError(res.status, data.error || `request failed: ${res.status}`, data.request_id)
  }
  return data as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  delete: <T>(path: string) => request<T>('DELETE', path),
}

// --- typed shapes matching pkg/api's response structs exactly -------------------------

export interface SignupResponse {
  user_id: number
  mail_configured: boolean
  // Whether the message actually left. A provider can be configured and still reject
  // every send, which is what an unverified sending domain looks like.
  mail_sent?: boolean
}

export interface LoginResponse {
  ok?: boolean
  totp_required?: boolean
  challenge?: string
}

export interface Me {
  email: string
  organization_id: number
  org_name: string
  credits: number
  totp_enabled: boolean
}

export interface CreditEntry {
  ID: number
  Reason: string
  Delta: number
  CreatedAt: string
  // The server has always sent this; the type left it out, so the reference to a payment
  // was in the response and never on the screen.
  Note?: string
}

export interface TOTPPending {
  secret: string
  otpauth_url: string
}

export const auth = {
  signup: (email: string, password: string, turnstileToken: string) =>
    api.post<SignupResponse>('/api/auth/signup', { email, password, turnstile_token: turnstileToken }),
  verify: (userId: number, code: string) =>
    api.post<{ ok: true }>('/api/auth/verify', { user_id: userId, code }),
  login: (email: string, password: string) =>
    api.post<LoginResponse>('/api/auth/login', { email, password }),
  loginTOTP: (challenge: string, code: string) =>
    api.post<{ ok: true }>('/api/auth/login/totp', { challenge, code }),
  logout: () => api.post<{ ok: true }>('/api/auth/logout'),
}

export const account = {
  me: () => api.get<Me>('/api/me'),
  creditsHistory: () => api.get<CreditEntry[]>('/api/credits/history'),
  redeemCoupon: (code: string) =>
    api.post<{ granted: number; test_mode_until?: string }>('/api/coupon', { code }),
}

export type CreditPack = {
  id: string
  label: string
  credits: number
  amount_cents: number
  currency: string
}

export const payments = {
  // enabled is false when no payment provider is configured, so the UI hides buying
  // rather than offering a button that cannot work.
  packs: () => api.get<{ enabled: boolean; packs: CreditPack[]; mode: 'live' | 'test' }>('/api/payments/packs'),
  // Returns a hosted checkout URL to redirect to. The browser sends only a pack id: what
  // that pack costs and grants is decided server-side.
  checkout: (pack: string) => api.post<{ url: string }>('/api/payments/checkout', { pack }),
}

export const totpSetup = {
  start: (password: string) => api.post<{ ok: true }>('/api/totp/setup', { password }),
  pending: () => api.get<TOTPPending>('/api/totp/setup/pending'),
  confirm: (code: string) => api.post<{ ok: true }>('/api/totp/setup/confirm', { code }),
}

export interface SiteRunEstimate {
  panel_id: number
  estimated_credits: number
}

export interface Site {
  id: number
  domain: string
  name: string
  description: string
  industry: string
  region: string
  verified_at: string | null
  // Measured as normal, kept out of the org-wide numbers.
  exclude_from_rollup?: boolean
  created_at: string
  // Present only when the site has a panel with at least one prompt: see
  // pkg/api/sites.go's runEstimatesForSites.
  run_estimate?: SiteRunEstimate
}

export type Cadence = 'off' | 'daily' | 'weekly' | 'monthly'

export interface SitePanel {
  id: number
  status: string
  languages: string[]
  models: ModelId[]
  repeats: number
  cadence: Cadence
  next_run_at: string | null
  // When a scheduled measurement last could not run for want of credits. This is the only
  // signal the customer gets that their trend has stopped, so it must be shown rather than
  // left as a silent gap in the chart.
  last_skipped_at: string | null
  prompts: { id: number; text: string; kind: string; position: number }[]
}

export interface SiteRun {
  id: number
  status: string
  turns_planned: number
  // A run shrunk to fit the balance. Its mention rate is not comparable to a full run's,
  // so it is labelled rather than shown as an ordinary measurement.
  limited_by_credits?: boolean
  turns_completed: number | null
  credits_charged: number | null
  created_at: string
  // How often the brand was actually mentioned, out of how many answers were measured -
  // the point of the run, as opposed to turns_completed's "did it finish".
  mentioned: number
  measured: number
}

export interface SiteDetail extends Site {
  panel: SitePanel | null
  runs: SiteRun[]
}

export const sites = {
  list: () => api.get<Site[]>('/api/sites'),
  get: (id: number) => api.get<SiteDetail>(`/api/sites/${id}`),
  setRollup: (id: number, exclude: boolean) =>
    api.put<{ exclude_from_rollup: boolean }>(`/api/sites/${id}/rollup`, { exclude_from_rollup: exclude }),
  create: (domain: string, name?: string) => api.post<Site>('/api/sites', { domain, name }),
  remove: (id: number) => api.delete<{ ok: true }>(`/api/sites/${id}`),
}

// --- onboarding wizard ------------------------------------------------------------

export interface BrandDetail {
  name: string
  description: string
  industry: string
  region: string
}

export interface SuggestedPrompt {
  text: string
  kind: 'core' | 'exploration' | 'comparative'
}

export const wizard = {
  // Both calls below block server-side for up to ~25s (a real Studio round trip): the
  // caller is expected to show a loading state, not a spinner-then-instant-response.
  fetchBrandDetail: (siteId: number) => api.post<BrandDetail>(`/api/sites/${siteId}/brand-detail`),
  suggestPrompts: (siteId: number, req: BrandDetail & { language: string; count: number }) =>
    api.post<{ prompts: SuggestedPrompt[] }>(`/api/sites/${siteId}/suggested-prompts`, req),
}

export type ModelId = 'chatgpt' | 'claude' | 'gemini'

export interface Panel {
  id: number
  site_id: number
  status: string
  languages: string[]
  models: ModelId[]
  repeats: number
  created_at: string
}

export interface CreatePanelRequest {
  site_id: number
  prompts: { text: string; kind: string }[]
  languages: string[]
  models: ModelId[]
  repeats: number
  competitors?: { name: string; domain?: string }[]
}

export const panels = {
  create: (req: CreatePanelRequest) => api.post<Panel>('/api/panels', req),
  // languages, when passed, scopes the run to only those languages: used to retry just
  // what failed (plan §5.9) instead of re-running (and re-charging for) everything.
  run: (panelId: number, languages?: string[]) =>
    api.post<{ run_id: number; turns_planned: number; credits_held: number }>(`/api/panels/${panelId}/run`, languages ? { languages } : undefined),
  setCadence: (panelId: number, cadence: Cadence) =>
    api.put<{ cadence: Cadence; next_run_at: string | null }>(`/api/panels/${panelId}/cadence`, { cadence }),
}

// --- rollup stats -----------------------------------------------------------------

export interface MentionStatsDailyPoint {
  date: string
  model: ModelId
  total: number
  mentioned: number
}

export interface MentionStatsBreakdown {
  key: string
  label: string
  total: number
  mentioned: number
}

export interface MentionStats {
  daily: MentionStatsDailyPoint[]
  by_model: MentionStatsBreakdown[]
  by_site?: MentionStatsBreakdown[]
  by_prompt?: MentionStatsBreakdown[]
}

export interface MentionStatsFilters {
  // siteIds only applies to the global (no siteId) view: meaningless once already
  // scoped to one site.
  siteIds?: number[]
  models?: ModelId[]
  languages?: string[]
  // promptIds narrows to specific questions; only meaningful in the single-site view,
  // where by_prompt is the breakdown dimension.
  promptIds?: string[]
  // from/to (YYYY-MM-DD) are the explicit date-range picker. Both must be set to take
  // effect; otherwise the backend's default "last N days" window applies.
  from?: string
  to?: string
}

export const stats = {
  // Omitting siteId gets the org-wide view (by_site instead of by_prompt); passing it
  // scopes everything to that one site (by_prompt instead of by_site): see
  // pkg/api/stats.go's getMentionStats for why this is one endpoint, not three. Every
  // filter (models/siteIds/languages) narrows daily AND every breakdown uniformly,
  // including a breakdown's own dimension (filtering models=claude means by_model, too,
  // only contains claude): this is a genuine "recompute over this subset" filter, not a
  // "which one to currently view" picker, so there's no dimension that needs to stay
  // unfiltered to remain pickable from.
  mentions: (siteId?: number, filters?: MentionStatsFilters) => {
    const params = new URLSearchParams()
    if (siteId) params.set('site_id', String(siteId))
    if (filters?.siteIds?.length) params.set('site_ids', filters.siteIds.join(','))
    if (filters?.models?.length) params.set('models', filters.models.join(','))
    if (filters?.languages?.length) params.set('languages', filters.languages.join(','))
    if (filters?.promptIds?.length) params.set('prompt_ids', filters.promptIds.join(','))
    if (filters?.from && filters?.to) {
      params.set('from', filters.from)
      params.set('to', filters.to)
    }
    const qs = params.toString()
    return api.get<MentionStats>(`/api/stats/mentions${qs ? `?${qs}` : ''}`)
  },
}

export interface Measurement {
  prompt_id: number
  // The question that produced this answer. Resolved server-side from the prompt set the
  // run was built with, so a prompt edited afterwards still shows what was actually asked.
  prompt_text?: string
  model: ModelId
  language: string
  repeat_index: number
  mentioned: boolean
  excerpt: string
  // What the ANSWER named as its references, not where the model believes its knowledge
  // came from. Absent when the answer named nothing.
  sources?: string[]
  // The full AI response, decrypted server-side for this response only: unlike excerpt
  // (only ever set when mentioned is true), this is present for every turn.
  raw_answer?: string
}

export interface Run {
  id: number
  panel_id: number
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'refunded'
  turns_planned: number
  turns_completed: number | null
  credits_held: number
  credits_charged: number | null
  error?: string
  failed_languages?: string[]
  measurements?: Measurement[]
  // What the mention check was looking for; the results view highlights it inside each
  // raw answer so a "Genoemd" verdict can be checked rather than trusted.
  brand_name?: string
}

export const runs = {
  get: (runId: number) => api.get<Run>(`/api/runs/${runId}`),
}

// --- API tokens ---------------------------------------------------------------------

export type ApiKeyScope = 'read' | 'readwrite'

export interface ApiKey {
  id: number
  name: string
  key_prefix: string
  scope: ApiKeyScope
  site_id: number | null
  site_name?: string
  // "admin" means the key was minted via the X-Admin-Secret-gated support path
  // (docs/plans/2026-09-03-admin-api-key-creation.md), never silently: shown as such
  // below so an org can always see a key it didn't create itself.
  created_via: 'self' | 'admin'
  last_used_at: string | null
  revoked_at: string | null
  created_at: string
}

export interface CreatedApiKey extends ApiKey {
  // Plaintext token: only ever present on this one response, see pkg/api/keys.go's
  // createAPIKeyResponse for why it's never retrievable again after this.
  key: string
}

export const apiKeys = {
  list: () => api.get<ApiKey[]>('/api/keys'),
  create: (name: string, scope: ApiKeyScope, siteId?: number) =>
    api.post<CreatedApiKey>('/api/keys', { name, scope, site_id: siteId ?? null }),
  revoke: (id: number) => api.post<{ ok: true }>(`/api/keys/${id}/revoke`),
}
